import { useState } from 'react';
import { Modal, Form, Button as BSButton, Alert } from 'react-bootstrap';
import Button from '../../UI/button';
import {
  Globe,
  Lock,
  Eye,
  EyeOff,
  Users,
  Link2,
  Map,
  Image,
  Search
} from 'lucide-react';
import { usePublishTrip } from './use-publish-trip';
import './make-trip-public-modal.scss';

type MakePublicConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  tripId: string;
  tripTitle: string;
};

export function MakePublicConfirmModal({
  isOpen,
  onClose,
  onSuccess,
  tripId,
  tripTitle
}: MakePublicConfirmModalProps) {
  const [keepNotesPrivate, setKeepNotesPrivate] = useState(true);
  const [visibleToFollowers, setVisibleToFollowers] = useState(true);
  const [shareableViaLink, setShareableViaLink] = useState(true);
  const [discoverableInSearch, setDiscoverableInSearch] = useState(false);
  const [defaultShareView, setDefaultShareView] = useState<'map' | 'photos'>(
    'photos'
  );

  const { publishTrip, isPublishing } = usePublishTrip();

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const success = await publishTrip(tripId, {
      excludeNotes: keepNotesPrivate,
      isDiscoverable: discoverableInSearch
    });

    if (success) {
      onSuccess?.();
      onClose();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center'
            style={{ width: '48px', height: '48px' }}
          >
            <Globe
              size={24}
              className='text-success'
            />
          </div>
          <div>
            <Modal.Title className='h5 mb-0'>Make Trip Public?</Modal.Title>
            <p className='small text-muted mb-0'>{tripTitle}</p>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Alert
          variant='info'
          className='mb-3'
        >
          <h6 className='fw-bold mb-2'>What will be public:</h6>
          <ul className='small mb-0'>
            <li>Trip title and dates</li>
            <li>All saved places and their locations</li>
            <li>Place addresses and websites</li>
            <li>Day-by-day itinerary</li>
          </ul>
        </Alert>

        <div className='d-flex flex-column gap-3'>
          <div className='border rounded p-2'>
            <Form.Check
              type='checkbox'
              id='discoverableInSearch'
              checked={discoverableInSearch}
              onChange={(e) => setDiscoverableInSearch(e.target.checked)}
              label={
                <div className='d-flex align-items-center gap-2'>
                  <Search
                    size={16}
                    className='text-success'
                  />
                  <span className='fw-medium'>
                    Discoverable in Public Search
                  </span>
                </div>
              }
            />
            <p className='small text-muted ms-4 mb-0'>
              Allow anyone to find this trip in search results and discover page
            </p>
          </div>

          <div className='border rounded p-2'>
            <Form.Check
              type='checkbox'
              id='visibleToFollowers'
              checked={visibleToFollowers}
              onChange={(e) => setVisibleToFollowers(e.target.checked)}
              label={
                <div className='d-flex align-items-center gap-2'>
                  <Users
                    size={16}
                    className='text-primary'
                  />
                  <span className='fw-medium'>Visible to Followers</span>
                </div>
              }
            />
            <p className='small text-muted ms-4 mb-0'>
              Allow your followers to discover and view this trip
            </p>
          </div>

          <div className='border rounded p-2'>
            <Form.Check
              type='checkbox'
              id='shareableViaLink'
              checked={shareableViaLink}
              onChange={(e) => setShareableViaLink(e.target.checked)}
              label={
                <div className='d-flex align-items-center gap-2'>
                  <Link2
                    size={16}
                    className='text-purple'
                  />
                  <span className='fw-medium'>Shareable via Link</span>
                </div>
              }
            />
            <p className='small text-muted ms-4 mb-0'>
              Anyone with the link can view this trip
            </p>
            {shareableViaLink && (
              <div className='ms-4 mt-3'>
                <p className='small fw-medium mb-2'>
                  Default view when shared:
                </p>
                <div className='d-flex gap-2'>
                  <Button
                    variant={
                      defaultShareView === 'photos'
                        ? 'primary'
                        : 'outline-secondary'
                    }
                    onClick={() => setDefaultShareView('photos')}
                    className='flex-fill d-flex align-items-center justify-content-center gap-2'
                    size='sm'
                  >
                    <Image size={16} />
                    <span className='small'>Photos</span>
                  </Button>
                  <Button
                    variant={
                      defaultShareView === 'map'
                        ? 'primary'
                        : 'outline-secondary'
                    }
                    onClick={() => setDefaultShareView('map')}
                    className='flex-fill d-flex align-items-center justify-content-center gap-2'
                    size='sm'
                  >
                    <Map size={16} />
                    <span className='small'>Map</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className='border rounded p-2'>
            <Form.Check
              type='checkbox'
              id='keepNotesPrivate'
              checked={keepNotesPrivate}
              onChange={(e) => setKeepNotesPrivate(e.target.checked)}
              label={
                <div className='d-flex align-items-center gap-2'>
                  {keepNotesPrivate ? (
                    <>
                      <EyeOff
                        size={16}
                        className='text-muted'
                      />
                      <span className='fw-medium'>
                        Keep personal notes private
                      </span>
                    </>
                  ) : (
                    <>
                      <Eye
                        size={16}
                        className='text-success'
                      />
                      <span className='fw-medium'>
                        Show personal notes publicly
                      </span>
                    </>
                  )}
                </div>
              }
            />
            <p className='small text-muted ms-4 mb-0'>
              {keepNotesPrivate
                ? 'Your personal notes will remain visible only to you'
                : 'Your personal notes on places will be visible to everyone'}
            </p>
          </div>
        </div>

        <Alert
          variant='warning'
          className='mt-2 mb-0'
        >
          <div className='d-flex align-items-start gap-2'>
            <Lock
              size={14}
              className='mt-1'
            />
            <small>
              You can change these settings or make your trip private again at
              any time
            </small>
          </div>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='dark'
          onClick={onClose}
          width={120}
          disabled={isPublishing}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleConfirm}
          width={120}
          disabled={isPublishing}
        >
          {isPublishing ? 'Publishing...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
